import type { JSX } from "react";
import type { RecipeIngredient, RecipeStep, StepTimerDef } from "../../shared/recipes";
import { ingredientLabelsForStep } from "./format-ingredient";
import { formatTimerDuration } from "./timer-display";
import type { ActiveTimer } from "./useRecipeTimers";

type StepCardProps = {
  step: RecipeStep;
  index: number;
  ingredients?: readonly RecipeIngredient[];
  expanded: boolean;
  canExpand: boolean;
  onToggleExpanded: () => void;
  done?: boolean;
  onToggleDone?: () => void;
  scaleFactor?: number;
  getActiveTimer?: (timerDefId: string) => ActiveTimer | undefined;
  getTimerRemainingSeconds?: (timer: ActiveTimer) => number;
  onStartTimer?: (def: StepTimerDef) => void;
  onPauseTimer?: (timerId: string) => void;
  onResumeTimer?: (timerId: string) => void;
  onCancelTimer?: (timerId: string) => void;
};

export function StepCard({
  step,
  index,
  ingredients = [],
  expanded,
  canExpand,
  onToggleExpanded,
  done = false,
  onToggleDone,
  scaleFactor = 1,
  getActiveTimer,
  getTimerRemainingSeconds,
  onStartTimer,
  onPauseTimer,
  onResumeTimer,
  onCancelTimer
}: StepCardProps): JSX.Element {
  const hasDetails = step.details !== undefined && step.details.length > 0;
  const stepIngredientLabels = ingredientLabelsForStep(step, ingredients, scaleFactor);
  const hasIngredients = stepIngredientLabels.length > 0;
  const timerDefs = step.timers ?? [];

  return (
    <article className={`step-card privacy-private${done ? " step-card-done" : ""}`}>
      <div className="step-card-main">
        {onToggleDone !== undefined ? (
          <button
            type="button"
            className="step-check"
            aria-label={done ? "Mark step not done" : "Mark step done"}
            aria-pressed={done}
            onClick={onToggleDone}
          >
            {done ? "✓" : ""}
          </button>
        ) : (
          <span className="step-number">{index + 1}</span>
        )}
        <div className="step-card-content">
          <div className="step-card-heading">
            <p className="step-summary">{step.summary}</p>
            {canExpand ? (
              <button
                type="button"
                className="step-expand-toggle"
                aria-expanded={expanded}
                aria-label={expanded ? "Collapse step" : "Expand step"}
                onClick={onToggleExpanded}
              >
                <span className="step-expand-icon" aria-hidden="true" />
              </button>
            ) : null}
          </div>
          {timerDefs.length > 0 ? (
            <ul className="step-timer-defs" aria-label="Timers for this step">
              {timerDefs.map((timer) => {
                const active = getActiveTimer?.(timer.id);
                const remaining =
                  active !== undefined && getTimerRemainingSeconds !== undefined
                    ? getTimerRemainingSeconds(active)
                    : undefined;
                return (
                  <li key={timer.id} className={active !== undefined ? "step-timer-active" : undefined}>
                    <span>
                      {timer.label ?? "Timer"}:{" "}
                      {remaining !== undefined
                        ? formatTimerDuration(remaining)
                        : formatTimerDuration(timer.durationSeconds)}
                    </span>
                    {onStartTimer !== undefined ? (
                      <div className="step-timer-actions">
                        {active === undefined ? (
                          <button type="button" className="text-button" onClick={() => { onStartTimer(timer); }}>
                            Start
                          </button>
                        ) : (
                          <>
                            {active.pausedAtMs === undefined ? (
                              <button
                                type="button"
                                className="text-button"
                                onClick={() => { onPauseTimer?.(active.id); }}
                              >
                                Pause
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="text-button"
                                onClick={() => { onResumeTimer?.(active.id); }}
                              >
                                Resume
                              </button>
                            )}
                            <button
                              type="button"
                              className="text-button"
                              onClick={() => { onCancelTimer?.(active.id); }}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {expanded && (hasDetails || hasIngredients) ? (
            <div className="step-card-expanded">
              {hasDetails ? <p className="step-details">{step.details}</p> : null}
              {hasIngredients ? (
                <section className="step-ingredients-section" aria-label="Ingredients for this step">
                  <h4 className="step-subsection-label">Ingredients</h4>
                  <ul className="step-ingredients">
                    {stepIngredientLabels.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}
